// src/hooks/useAuthSync.js
// Wires Firebase Auth state to the user store.
// On sign-in: pulls saved zones/prefs from Firestore and restores them locally.
// On data change: writes zone/zones to Firestore so they sync across devices.

import { useEffect, useRef } from 'react';
import { useUserStore }      from '@store';

export function useAuthSync() {
  const zone          = useUserStore((s) => s.zone);
  const zones         = useUserStore((s) => s.zones);
  const phoneVerified = useUserStore((s) => s.phoneVerified);
  const profileId     = useUserStore((s) => s.profileId);
  const setZone       = useUserStore((s) => s.setZone);
  const addZone       = useUserStore((s) => s.addZone);

  const uidRef        = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    let unsub = null;

    async function init() {
      const { getAuth, onAuthStateChanged } = await import('firebase/auth');
      const { getApp } = await import('firebase/app');
      const auth = getAuth(getApp());

      unsub = onAuthStateChanged(auth, async (user) => {
        if (!user || user.isAnonymous) {
          uidRef.current = null;
          initializedRef.current = false;
          return;
        }

        const uid = user.uid;
        uidRef.current = uid;

        // Pull from Firestore on first sign-in / new device
        try {
          const { getFirestore, doc, getDoc, setDoc, serverTimestamp } =
            await import('firebase/firestore');
          const db  = getFirestore(getApp());
          const ref = doc(db, 'users', uid);
          const snap = await getDoc(ref);

          if (snap.exists()) {
            const data = snap.data();
            // Restore zones if Firestore has data and local is empty/default
            if (data.zone && !useUserStore.getState().zone) {
              setZone(data.zone);
            }
            if (data.zones?.length > 0 && useUserStore.getState().zones.length <= 1) {
              data.zones.forEach(z => addZone(z));
            }
          }

          // Write current local state to Firestore (create or update)
          const localZone  = useUserStore.getState().zone;
          const localZones = useUserStore.getState().zones;
          await setDoc(ref, {
            uid,
            email:         user.email ?? null,
            displayName:   user.displayName ?? null,
            zone:          localZone ?? null,
            zones:         localZones ?? [],
            phoneVerified: useUserStore.getState().phoneVerified,
            lastSeenAt:    serverTimestamp(),
            updatedAt:     serverTimestamp(),
          }, { merge: true });

          initializedRef.current = true;
        } catch (err) {
          console.warn('[useAuthSync] Firestore sync failed:', err.message);
        }
      });
    }

    init().catch(() => {});
    return () => unsub?.();
  }, []);

  // Write zone changes to Firestore when user is signed in
  useEffect(() => {
    if (!initializedRef.current || !uidRef.current || !zone) return;

    async function syncZones() {
      try {
        const { getFirestore, doc, updateDoc, serverTimestamp } =
          await import('firebase/firestore');
        const { getApp } = await import('firebase/app');
        const db  = getFirestore(getApp());
        const ref = doc(db, 'users', uidRef.current);
        await updateDoc(ref, { zone, zones, updatedAt: serverTimestamp() });
      } catch {}
    }

    syncZones();
  }, [zone, JSON.stringify(zones)]);
}
